import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useProjects } from '../contexts/ProjectContext';
import { useI18n } from '../i18n/I18nContext';
import { useProMessages } from '../i18n/pro';
import { getLedgerEntries, LedgerEntry } from '../firebase/firestore';
import './Ledger.css';

const Ledger: React.FC = () => {
  const { firebaseUser } = useAuth();
  const { includesInvoice, selectedProject } = useProjects();
  const { t } = useI18n();
  const proLabels = useProMessages().app;
  const [entries, setEntries] = useState<LedgerEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (firebaseUser) {
      loadLedger();
    }
  }, [firebaseUser]);

  const loadLedger = async () => {
    if (!firebaseUser) return;
    
    try {
      setLoading(true);
      const data = await getLedgerEntries(firebaseUser.uid);
      setEntries(data);
      setError(null);
    } catch (err: any) {
      setError(err.message || t.app.ledger.failedLoad);
    } finally {
      setLoading(false);
    }
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'GROSS':
        return t.app.ledger.typePayment;
      case 'FEE':
        return t.app.ledger.typeFee;
      case 'NET':
        return t.app.ledger.typeNetPayment;
      case 'PAYOUT':
        return t.app.ledger.typeWithdrawal;
      case 'ADJUSTMENT':
        return t.app.ledger.typeAdjustment;
      default:
        return type;
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'GROSS':
      case 'NET':
        return 'type-positive';
      case 'FEE':
      case 'PAYOUT':
        return 'type-negative';
      case 'ADJUSTMENT':
        return 'type-neutral';
      default:
        return '';
    }
  };

  const formatDate = (dateString: Date | string) => {
    const date = typeof dateString === 'string' ? new Date(dateString) : dateString;
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const visibleEntries = entries.filter((entry) =>
    entry.invoiceId ? includesInvoice(entry.invoiceId) : !selectedProject,
  );

  if (loading) {
    return <div className="ledger-loading">{t.app.ledger.loading}</div>;
  }

  if (error) {
    return <div className="ledger-error">{t.app.common.error}: {error}</div>;
  }

  return (
    <div className="ledger">
      <div className="ledger-header">
        <h1>{t.app.ledger.title}</h1>
        <p className="ledger-description">
          {t.app.ledger.description}
        </p>
      </div>

      {visibleEntries.length === 0 ? (
        <div className="no-entries">
          <p>{t.app.ledger.noEntries}</p>
        </div>
      ) : (
        <div className="ledger-table">
          <table>
            <thead>
              <tr>
                <th>{t.app.common.date}</th>
                <th>{t.app.ledger.typeColumn}</th>
                <th>{t.app.ledger.amountStars}</th>
                <th>{t.app.common.details}</th>
              </tr>
            </thead>
            <tbody>
              {visibleEntries.map((entry) => (
                <tr key={entry.id}>
                  <td>{formatDate(entry.createdAt)}</td>
                  <td>
                    <span className={`type-badge ${getTypeColor(entry.type)}`}>
                      {getTypeLabel(entry.type)}
                    </span>
                    {entry.direct && <span className="type-badge type-direct">{proLabels.directBadge}</span>}
                  </td>
                  <td className={entry.amountStars >= 0 ? 'amount-positive' : 'amount-negative'}>
                    {entry.amountStars >= 0 ? '+' : ''}{entry.amountStars} {t.app.common.stars}
                  </td>
                  <td className="entry-details">
                    {entry.invoice && (
                      <span>
                        {t.app.ledger.invoiceDetail
                          .replace('{title}', entry.invoice.title || entry.invoice.description || t.app.common.noTitle)
                          .replace('{amount}', String(entry.invoice.starsAmount))}
                        {entry.invoice.description && entry.invoice.title && ` - ${entry.invoice.description}`}
                        {entry.payerTelegramUserId && (
                          <span className="payer-info">
                            {' • '}{t.app.ledger.paidBy.replace('{payer}', entry.payerName || entry.payerUsername || `@${entry.payerTelegramUserId}`)}
                            {entry.payerTelegramUserId && (
                              <span className="payer-id"> {t.app.ledger.payerId.replace('{id}', String(entry.payerTelegramUserId))}</span>
                            )}
                          </span>
                        )}
                      </span>
                    )}
                    {entry.withdraw && (
                      <span>
                        {t.app.ledger.withdrawDetail
                          .replace('{amount}', String(entry.withdraw.amountStars))
                          .replace('{status}', entry.withdraw.status)}
                      </span>
                    )}
                    {!entry.invoice && !entry.withdraw && (
                      <span className="no-details">-</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default Ledger;
